import { ChevronsDownUp, ChevronsUpDown, History, Search, Zap } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";

import {
  DELIVERY_STATUS_LABEL,
  DELIVERY_STATUS_ORDER,
  GROUP_BY_OPTIONS,
  type BoardGroupBy,
} from "@/presentation/data/board";
import { DOMAIN_LABEL, DOMAINS, type Domain } from "@/presentation/data/domains";
import type { DeliveryStatus } from "@/infrastructure/supabase/database.types";

export interface BoardFilters {
  search: string;
  status: DeliveryStatus | "all";
  domain: Domain | "all";
  criticalOnly: boolean;
}

interface BoardToolbarProps {
  groupBy: BoardGroupBy;
  onGroupByChange: (next: BoardGroupBy) => void;
  filters: BoardFilters;
  onFiltersChange: (next: BoardFilters) => void;
  onOpenActivity: () => void;
  /** True when every rendered group is collapsed — flips the toggle to "Expand all". */
  allCollapsed: boolean;
  onToggleAllGroups: () => void;
}

export function BoardToolbar({
  groupBy,
  onGroupByChange,
  filters,
  onFiltersChange,
  onOpenActivity,
  allCollapsed,
  onToggleAllGroups,
}: BoardToolbarProps) {
  const set = (patch: Partial<BoardFilters>) => onFiltersChange({ ...filters, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-2 print:hidden">
      <div className="flex items-center gap-2">
        <span className="eyebrow">Group by</span>
        <Select value={groupBy} onValueChange={(v) => onGroupByChange(v as BoardGroupBy)}>
          <SelectTrigger className="h-9 w-[150px] text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GROUP_BY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} className="text-[13px]">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
        <Input
          value={filters.search}
          onChange={(e) => set({ search: e.target.value })}
          placeholder="Search features…"
          className="h-9 w-[200px] pl-8 text-[13px]"
        />
      </div>

      <Select
        value={filters.status}
        onValueChange={(v) => set({ status: v as DeliveryStatus | "all" })}
      >
        <SelectTrigger className="h-9 w-[140px] text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            All statuses
          </SelectItem>
          {DELIVERY_STATUS_ORDER.map((s) => (
            <SelectItem key={s} value={s} className="text-[13px]">
              {DELIVERY_STATUS_LABEL[s]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={filters.domain} onValueChange={(v) => set({ domain: v as Domain | "all" })}>
        <SelectTrigger className="h-9 w-[170px] text-[13px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" className="text-[13px]">
            All domains
          </SelectItem>
          {DOMAINS.map((d) => (
            <SelectItem key={d} value={d} className="text-[13px]">
              {DOMAIN_LABEL[d]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant={filters.criticalOnly ? "default" : "outline"}
        size="sm"
        className="h-9 gap-1.5"
        onClick={() => set({ criticalOnly: !filters.criticalOnly })}
      >
        <Zap className="size-3.5" />
        Critical path
      </Button>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-auto h-9 gap-1.5"
        onClick={onToggleAllGroups}
      >
        {allCollapsed ? (
          <ChevronsUpDown className="size-3.5" />
        ) : (
          <ChevronsDownUp className="size-3.5" />
        )}
        {allCollapsed ? "Expand all" : "Collapse all"}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 gap-1.5"
        onClick={onOpenActivity}
      >
        <History className="size-3.5" />
        Activity
      </Button>
    </div>
  );
}
